import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams, RowNode } from '@ag-grid-community/core';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, WritableSignal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { IconComponent } from '@shared/components/icon/icon.component';
import { ExpenseSourceType } from '@shared/services/gql.service';

interface AgExpenseDefaultsActionComponentParams extends ICellRendererParams {
  dragDropChangeFN: (params: { rowNode: RowNode }) => void;
  editable: WritableSignal<boolean>;
  processing$?: Observable<boolean>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DragDropModule, AsyncPipe, NgClass, IconComponent, NgStyle],
  styleUrls: ['ag-expense-defaults-drag.component.scss'],
  templateUrl: 'ag-expense-defaults-drag.component.html',
})
export class AgExpenseDefaultsDragComponent implements ICellRendererAngularComp {
  readonly bgClasses: string = 'border rounded-[5px] w-[120px] cursor-move';
  readonly bgClassesNonEditable: string = 'border rounded-[5px] w-[120px] cursor-not-allowed';
  dataCheck$ = new BehaviorSubject(false);
  lineData: ExpenseSourceType[] = [];
  optionData = new BehaviorSubject<ExpenseSourceType[]>([]);
  params!: AgExpenseDefaultsActionComponentParams;
  readonly txtClasses: string = 'max-w whitespace-nowrap overflow-hidden text-ellipsis';

  agInit(params: AgExpenseDefaultsActionComponentParams) {
    this.params = params;
    this.dataCheck$.next(!!this.params.data);

    if (this.params?.data?.sources) {
      this.lineData = [...this.params.data.sources];
      this.optionData.next(this.lineData);
    }
  }

  get(timePeriod: ExpenseSourceType, editable: boolean | null) {
    const selectedBgClasses = editable ? this.bgClasses : this.bgClassesNonEditable;

    switch (timePeriod) {
      case ExpenseSourceType.EXPENSE_SOURCE_EVIDENCE_BASED:
        return {
          bg: `border-aux-green-dark bg-white ${selectedBgClasses}`,
          text: 'Evidence Based',
          txtClass: `text-aux-green-dark ${this.txtClasses}`,
        };
      case ExpenseSourceType.EXPENSE_SOURCE_FORECAST:
        return {
          bg: `border-aux-blue bg-aux-blue-light bg-opacity-25 ${selectedBgClasses}`,
          text: 'Forecast',
          txtClass: `text-aux-blue ${this.txtClasses}`,
        };
      case ExpenseSourceType.EXPENSE_SOURCE_INVOICE:
        return {
          bg: `border-aux-blue bg-white ${selectedBgClasses}`,
          text: 'Invoice',
          txtClass: `text-aux-blue ${this.txtClasses}`,
        };
      case ExpenseSourceType.EXPENSE_SOURCE_VENDOR_ESTIMATE:
        return {
          bg: `border-aux-green-dark bg-aux-gray-dark-300 bg-opacity-25 ${selectedBgClasses}`,
          text: 'Vendor Estimate',
          txtClass: `text-aux-green-dark ${this.txtClasses}`,
        };
      default:
        return { bg: '', text: '', txtClass: '' };
    }
  }

  noReturnPredicate() {
    return false;
  }

  onDragDropChange(event: CdkDragDrop<string[]>) {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.lineData, event.previousIndex, event.currentIndex);

      if (this.params.dragDropChangeFN) {
        this.params.dragDropChangeFN({
          rowNode: {
            ...this.params.node,
            data: { ...this.params.node.data, sources: this.lineData },
          } as RowNode,
        });
      }
    }
  }

  refresh(): boolean {
    return false;
  }
}
